'use client'

import { useEffect } from 'react'

import { captureClientException } from '@/lib/analytics/capture-client-exception'
import {
  hardReloadForStaleDeploy,
  isLikelyStaleDeployError,
  tryRecoverFromStaleDeploy
} from '@/lib/utils/stale-deploy-recovery'

export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // A stale-deploy/chunk/router-cache mismatch self-heals with a fresh build:
    // hard reload once (cooldown-guarded) instead of stranding the user.
    if (isLikelyStaleDeployError(error) && tryRecoverFromStaleDeploy()) {
      return
    }

    captureClientException(error, {
      source: 'global-error-boundary',
      boundary: 'global-error-boundary',
      severity: 'fatal'
    })
  }, [error])

  return (
    <html>
      <body>
        <div className="flex h-screen flex-col items-center justify-center gap-3 p-4 text-center">
          <h1 className="text-foreground text-2xl font-semibold">
            Something went wrong
          </h1>
          <p className="text-muted-foreground max-w-md text-sm">
            The app hit an unexpected error. You can retry the current action or
            head back home.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => {
                if (isLikelyStaleDeployError(error)) {
                  hardReloadForStaleDeploy()
                  return
                }
                reset()
              }}
              className="bg-primary text-primary-foreground rounded px-4 py-2 text-sm font-medium"
            >
              Retry
            </button>
            <button
              onClick={() => window.location.assign('/')}
              className="bg-secondary text-secondary-foreground rounded px-4 py-2 text-sm font-medium"
            >
              Go Home
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
